import React, { useState, useEffect } from 'react'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown'
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp'

import styled from '@emotion/styled'
import AccordionContent from './AccordionContent'
import { isInStorefront, getLocationHash } from '../../utils/isInStorefront'

let Icon = () => (
    <div className="flex items-center justify-center w-full py-10">
        <FontAwesomeIcon icon={faChevronDown} size="6x" />
    </div>
)

const FONT_SIZES = ['xs', 'sm', 'md', 'lg', 'xl', '2xl']

// Defined once at module level: creating it inside the component gave the header a new component
// type on every render, so it was remounted each time the block re-rendered (e.g. when focused)
const StyledHeader = styled(DisclosureButton, {
    shouldForwardProp: prop => prop !== 'colours',
})`
    background-color: ${({ colours }) => colours.background};
    color: ${({ colours }) => colours.text};

    &:hover {
        color: ${({ colours }) => colours.hoverText};
        background-color: ${({ colours }) => colours.hoverBackground};
    }
`

const AccordionComponent = ({
    state: {
        headerColour = '#000000',
        headerHoverColour,
        headerFontWeight = 'normal',
        headerFontSize = 3,
        headerBackgroundColour = 'transparent',
        headerBackgroundHoverColour,
        headerFont = 'body',
        borderColour = '',
        accordion = [],
        selectedAccordionIndex = 0,
    },
    isEditMode,
    focused,
    onChange,
}) => {
    const [internalAccordionIndex, setInternalAccordionIndex] = useState(
        selectedAccordionIndex || 0
    )
    const inStorefront = isInStorefront()
    const defaultIndexes = accordion.map((acc, idx) => (acc.openByDefault ? idx : null))

    const locationHash = getLocationHash()

    const anchorIndex = accordion.findIndex(acc => {
        if (locationHash) {
            return acc.anchor === locationHash.replace('#', '')
        } else {
            return false
        }
    })

    useEffect(() => {
        if (locationHash) {
            document.querySelector(locationHash)?.scrollIntoView()
        }
    }, [locationHash])

    return (
        <>
            {accordion?.length > 0 ? (
                <div data-testid="accordion">
                    {accordion?.map((item, index) => (
                        <Disclosure key={`accordion-${index}`} defaultOpen={item.openByDefault}>
                            {({ open }) => (
                                <div
                                    className={`content__accordion`}
                                    data-testid={`accordion__item-${index}`}
                                    style={{
                                        boxShadow:
                                            selectedAccordionIndex === index &&
                                            focused &&
                                            'inset 0 0 0 3px yellow',
                                        borderBottom: borderColour ? '1px solid' : 'none',
                                        borderColor: borderColour || 'transparent',
                                    }}
                                >
                                    <StyledHeader
                                        colours={{
                                            background: headerBackgroundColour,
                                            text: headerColour,
                                            hoverText: headerHoverColour || headerColour,
                                            hoverBackground:
                                                headerBackgroundHoverColour ||
                                                headerBackgroundColour,
                                        }}
                                        className={`w-full active:border-0 focus:outline-none pt-[10px] px-[15px] pb-[15px]`}
                                        {...(isEditMode && {
                                            onClick: () => {
                                                setInternalAccordionIndex(index)
                                                onChange({ selectedAccordionIndex: index })
                                            },
                                        })}
                                        style={{
                                            fontSize: FONT_SIZES[headerFontSize],
                                            fontWeight: headerFontWeight,
                                        }}
                                        data-testid={`accordion__item-${index}--header`}
                                    >
                                        <div
                                            className={` flex items-center justify-between font-bold text-lg`}
                                        >
                                            {item.label}
                                            <FontAwesomeIcon
                                                icon={open ? faChevronUp : faChevronDown}
                                            />
                                        </div>
                                    </StyledHeader>

                                    <DisclosurePanel>
                                        <div className={`my-[15px] px-[15px]`}>
                                            <AccordionContent content={item.text} />
                                        </div>
                                    </DisclosurePanel>
                                </div>
                            )}
                        </Disclosure>
                    ))}
                </div>
            ) : inStorefront ? (
                <></>
            ) : (
                <Icon />
            )}
        </>
    )
}

export default AccordionComponent
